import type { IParticipantPerson } from 'Interfaces/participantPerson';
import type { IMessageReferences } from 'Models/MessageModel';
import React from 'react';
import {
  ReactOutputRule,
  Rules,
  defaultRules,
  parserFor,
  reactFor,
  ruleOutput,
} from 'simple-markdown';
import { bugsnagClient } from 'Utils/logUtils';
import {
  buildAudioLink,
  buildCodeBlock,
  buildCodeSnippet,
  buildEmail,
  buildHeading,
  buildImageLink,
  buildInlineVideo,
  buildLinkText,
  buildMention,
  buildMentionKeyword,
  buildParagraph,
  buildRefLinkText,
  buildReplyBox,
  buildText,
  buildUnderline,
  buildUrl,
  buildVideoLink,
} from '.';

/**
 * Compile a Markdown formatted string to either React (or in the future, HTML, still working on that - RP 2018-11-18)
 *
 * @param content The input string to be compiled
 * @param includeMentions Whether to include rules for processing @Mentions (ex. only in `Channel` mode)
 * @param participantPersons An array of wrapped `ParticipantModel`s, linked with their `PersonModel`s, used for Mentions.
 * @param buildLinkPreview An `action` which should load the link preview for the Message model, given the URI.
 * @param references An array of references for processing replies.
 * @returns `React.ReactNode`
 */

export type compileViewerMarkdownProps = (props: {
  content: string;
  includeMentions: boolean;
  participantPersons?: IParticipantPerson[];
  buildLinkPreview?: (nodeTarget: string) => void;
  references: IMessageReferences[];
}) => React.ReactNode;

const normalizePlainTextLinks = (text: string): string => {
  // Don't modify the text - let the markdown parser handle it
  // This preserves the original text for display while buildLinkText adds protocol internally
  return text;
};

export const compileViewerMarkdown: compileViewerMarkdownProps = ({
  buildLinkPreview,
  content,
  includeMentions,
  participantPersons,
  references,
}) => {
  // if message contains no text to display
  if (!content) return null;

  try {
    const markDownRules = {
      ...defaultRules,
      // Disable default autolink rule that might capture emails
      autolink: { ...defaultRules.autolink, match: () => null },
      // Custom text rule that splits on URLs, emails, and mentions (only if mentions are enabled)
      text: buildText(includeMentions),
      // Custom URL rule that processes plain URLs
      url: buildUrl(),
      // Add email rule with higher priority (lower order number) than link rule
      email: buildEmail(),
      underline: buildUnderline(),
      singleBacktick: buildCodeSnippet(),
      codeBlock: buildCodeBlock(),
      heading: buildHeading(),
      paragraph: buildParagraph(),
      link: buildLinkText(buildLinkPreview),
      reflink: buildRefLinkText(),
      inlineVideoRule: buildInlineVideo(),
      imageLinkRule: buildImageLink(),
      videoLinkRule: buildVideoLink(),
      audioLinkRule: buildAudioLink(),
      ...(references.length && { replyBoxRule: buildReplyBox(references) }),
      ...(includeMentions && {
        mentionRule: buildMention(participantPersons),
        mentionKeywordRule: buildMentionKeyword(),
      }),
    } as Rules<ReactOutputRule>;

    const normalizedContent = normalizePlainTextLinks(content);
    const markDownParse = parserFor(markDownRules);
    const markDownOutput = reactFor(ruleOutput(markDownRules, 'react'));
    // Many rules require content to end in \n\n to be interpreted as a block.
    const parseTree = markDownParse(normalizedContent + '\n\n', {
      inline: true,
    });
    return markDownOutput(parseTree);
  } catch (error) {
    // Notify Bugsnag about the markdown compilation error
    bugsnagClient.notify(error, (event) => {
      event.severity = 'warning';
      event.context = 'compileViewerMarkdown';
      event.addMetadata('markdown', {
        contentLength: content?.length,
        includeMentions,
        hasReferences: references?.length > 0,
      });
    });

    // Fallback: return content as plain text if markdown compilation fails
    // This ensures the app never crashes due to malformed input
    return <>{content}</>;
  }
};
